import React, { useRef } from 'react';
import Editor, { BtnBold, BtnBulletList, BtnItalic, BtnNumberedList, Separator, Toolbar } from 'react-simple-wysiwyg';
import DOMPurify from 'dompurify';
import { BtnLinkDialog } from './RichTextField';
import './RichTextField.css';
import './MergeFieldRichTextField.css';

// Email Template merge fields (story #236) — record-derived only, per
// vis_email_templates.md Concept 3: no free-form/request-specific tokens,
// just these three. `key` doubles as the {{key}} subject-line token and the
// body chip's data-merge-field value, so both fields identify the same
// token the same way for whichever future story parses them at send time.
export const EMAIL_TEMPLATE_MERGE_FIELDS = [
  { key: 'first_name', label: 'Customer First Name' },
  { key: 'company_name', label: 'Company Name' },
  { key: 'sender_name', label: 'Sender Name' },
];

// A Quote template's own extra fields (issue #332), filled from the Quote
// being emailed — offered only on templates used for Quotes, since no other
// template is ever sent with a Quote. Payment Details is each Payment Option
// that's on, one detail per line, for a pay-first Quote email.
export const QUOTE_EMAIL_MERGE_FIELDS = [
  { key: 'quote_number', label: 'Quote Number' },
  { key: 'valid_until', label: 'Valid Until' },
  { key: 'quote_total', label: 'Quote Total' },
  { key: 'po_reference', label: 'PO Reference' },
  { key: 'bank_details', label: 'Payment Details' },
];

// Which merge fields a template offers — the Quote ones only on a template
// used for Quotes.
export function mergeFieldsForTemplate(forQuotes) {
  return forQuotes ? [...EMAIL_TEMPLATE_MERGE_FIELDS, ...QUOTE_EMAIL_MERGE_FIELDS] : EMAIL_TEMPLATE_MERGE_FIELDS;
}

// Placeholder values for the authoring screen's live preview only — not
// real record data (there's no record to pull from while authoring a
// template in the abstract). Compose-time preview against an actual
// Commercial Entity is a separate future story.
const MERGE_FIELD_SAMPLE_VALUES = {
  first_name: 'Jane',
  company_name: 'Acme Pty Ltd',
  sender_name: 'Alex Reid',
  quote_number: 'QO-00123',
  valid_until: '30/11/2026',
  quote_total: '$1,234.56 (inc GST)',
  po_reference: 'PO-4567',
  bank_details: ['Account Name: Acme Pty Ltd', 'BSB: 062-000', 'Account Number: 12345678', 'Use QO-00123 as your payment reference.'],
};

// Inserts a locked, atomic merge-field chip — rather than plain editable
// token text — so a stray keystroke can never silently corrupt the token
// name and break the merge later with no warning. Same insert-at-Range
// mechanics as RichTextField.js's @mention link, just a non-editable span
// instead of a clickable one, so it isn't confused with a hyperlink.
function insertChipAtSelection(editorEl, field) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !editorEl.contains(sel.anchorNode)) return;
  const range = sel.getRangeAt(0);
  range.deleteContents();

  const chip = document.createElement('span');
  chip.setAttribute('contenteditable', 'false');
  chip.setAttribute('data-merge-field', field.key);
  chip.className = 'merge-field-chip';
  chip.textContent = field.label;
  const trailingSpace = document.createTextNode(' ');
  const frag = document.createDocumentFragment();
  frag.appendChild(chip);
  frag.appendChild(trailingSpace);
  range.insertNode(frag);

  const after = document.createRange();
  after.setStartAfter(trailingSpace);
  after.collapse(true);
  sel.removeAllRanges();
  sel.addRange(after);

  // react-simple-wysiwyg's change-detection is wired to onInput (see its
  // ContentEditable) — this DOM mutation didn't come from a keystroke, so
  // it needs a manually dispatched input event to be picked up at all.
  editorEl.dispatchEvent(new Event('input', { bubbles: true }));
}

// Body field for an Email Template — same base editor and buttons as
// RichTextField.js (minus its @mention) plus one insert button per merge
// field. Unlike RichTextField's @mention, insertion is a deliberate
// button click, not an autocomplete trigger — Concept 3's merge fields are
// a small fixed list, not a searched set.
function MergeFieldRichTextField({ value, onChange, mergeFields, placeholder, disabled }) {
  const editorRef = useRef(null);

  // Toolbar buttons sit outside the contentEditable, so an ordinary click
  // would blur it and collapse the selection before the insert could use
  // it. preventDefault on mousedown keeps focus (and the current Range) in
  // the editor — the same trick the mention dropdown's onMouseDown uses.
  const handleInsert = (field) => (e) => {
    e.preventDefault();
    if (editorRef.current) insertChipAtSelection(editorRef.current, field);
  };

  return (
    <Editor
      ref={editorRef}
      containerProps={{ className: 'rich-text-field merge-field-rich-text' }}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
    >
      <Toolbar>
        <BtnBold />
        <BtnItalic />
        <BtnLinkDialog />
        <BtnBulletList />
        <BtnNumberedList />
        {mergeFields.length > 0 && <Separator />}
        {mergeFields.map((field) => (
          <button
            key={field.key}
            type="button"
            className="merge-field-btn"
            onMouseDown={handleInsert(field)}
            disabled={disabled}
            title={`Insert ${field.label}`}
          >
            + {field.label}
          </button>
        ))}
      </Toolbar>
    </Editor>
  );
}

// Plain-text counterpart for the Subject field, which can't hold a DOM
// chip — inserts the literal {{key}} token at the input's cursor position
// instead. Still only reachable via the same button (no free-typing
// encouraged), just without the body's atomic-chip guarantee, which a
// single-line text input has no way to offer.
export function insertSubjectToken(inputEl, field, value, onChange) {
  const start = inputEl?.selectionStart ?? value.length;
  const end = inputEl?.selectionEnd ?? value.length;
  const token = `{{${field.key}}}`;
  const next = value.slice(0, start) + token + value.slice(end);
  onChange(next);
  requestAnimationFrame(() => {
    if (!inputEl) return;
    const caret = start + token.length;
    inputEl.focus();
    inputEl.setSelectionRange(caret, caret);
  });
}

// Shared by the authoring screen's sample-data preview (renderMergePreview,
// below) and Compose Email's real-data merge-fill (renderRealMergePreview,
// issue #238) — same {{key}} subject-token regex and [data-merge-field]
// body-chip DOM-replace either way, just a different values map. A key
// present in `values` fills even when blank (e.g. a Quote with no PO
// Reference); only a key with no value at all keeps its token/label. A value
// given as a list of lines (Payment Details) fills the body one line each, and
// the subject comma-separated.
function fillMergeFields(subject, bodyHtml, values) {
  const has = (key) => values[key] !== undefined && values[key] !== null;
  const asText = (key) => (Array.isArray(values[key]) ? values[key].join(', ') : String(values[key]));
  const previewSubject = (subject || '').replace(/\{\{(\w+)\}\}/g, (match, key) => (has(key) ? asText(key) : match));

  const doc = new DOMParser().parseFromString(DOMPurify.sanitize(bodyHtml || ''), 'text/html');
  doc.querySelectorAll('[data-merge-field]').forEach((el) => {
    const key = el.getAttribute('data-merge-field');
    if (has(key) && Array.isArray(values[key])) {
      const lines = values[key].flatMap((line, i) => (i ? [doc.createElement('br'), doc.createTextNode(line)] : [doc.createTextNode(line)]));
      el.replaceWith(...lines);
      return;
    }
    el.replaceWith(document.createTextNode(has(key) ? String(values[key]) : el.textContent));
  });
  const previewBodyHtml = doc.body.innerHTML;

  return { previewSubject, previewBodyHtml };
}

// Live preview (authoring screen only) — substitutes sample placeholder
// values for both the subject's {{key}} tokens and the body's merge-field
// chips, so an admin can sanity-check wording before saving.
export function renderMergePreview(subject, bodyHtml) {
  return fillMergeFields(subject, bodyHtml, MERGE_FIELD_SAMPLE_VALUES);
}

// Compose Email's real-data merge-fill (issue #238) — same mechanics as
// renderMergePreview above, fed real values off the picked Commercial
// Entity/contact/sender instead of the authoring screen's sample data.
export function renderRealMergePreview(subject, bodyHtml, values) {
  return fillMergeFields(subject, bodyHtml, values || {});
}

export default MergeFieldRichTextField;
